import React, { useEffect, useRef, useState } from 'react';
import { ShieldAlert, PencilLine, ShieldCheck, Check } from 'lucide-react';
import AiDialog from './AiDialog';
import PhiMatchList from './PhiMatchList';

/**
 * 400 PHI_SUSPECTED: the server found text the automatic scrubber can't remove reliably (a name next to "wife", a
 * free-text address, an age over 89 in words, a facility name). Snippets arrive masked.
 * `onEdit()` returns to the text; `onConfirm()` re-sends once with X-PHI-Confirm: 1.
 */
export default function AiPhiDialog({ open, matches = [], onEdit, onConfirm }) {
  const [checked, setChecked] = useState(false);
  const editRef = useRef(null);

  useEffect(() => {
    if (open) setChecked(false);
  }, [open]);

  return (
    <AiDialog open={open} onClose={onEdit} labelledBy="ai-phi-title" describedBy="ai-phi-desc" initialFocusRef={editRef} closeLabel="Edit text">
      <div className="border-b border-line/15 bg-navy px-5 pb-5 pt-6 text-on-navy sm:px-6">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sun px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-sun-ink">
          <ShieldAlert className="h-3.5 w-3.5" aria-hidden="true" /> Possible identifiers
        </span>
        <h2 id="ai-phi-title" className="mt-3 pr-8 font-display text-2xl font-extrabold leading-tight text-on-navy">
          This text may still identify the patient
        </h2>
        <p id="ai-phi-desc" className="mt-1.5 text-sm text-on-navy/80">
          Our scrubber removes labelled identifiers, but it can't safely remove these on its own. Nothing was sent to the AI and no credits were used.
        </p>
      </div>

      <div className="px-5 py-5 sm:px-6">
        <PhiMatchList matches={matches} />

        <label
          htmlFor="ai-phi-check"
          className={`mt-5 flex cursor-pointer items-start gap-3 rounded-2xl border p-3.5 text-sm transition-colors ${checked ? 'border-ok/40 bg-ok-soft' : 'border-line/30 bg-surface-2 hover:border-primary/40'}`}
        >
          <span className="relative mt-0.5 grid h-5 w-5 shrink-0 place-items-center">
            <input
              id="ai-phi-check"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="peer h-5 w-5 cursor-pointer appearance-none rounded-md border-2 border-line/60 bg-surface transition-colors checked:border-ok checked:bg-ok focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
            />
            <Check className="pointer-events-none absolute h-3.5 w-3.5 text-canvas opacity-0 peer-checked:opacity-100" strokeWidth={3.5} aria-hidden="true" />
          </span>
          <span className="font-medium text-ink">I checked these. They are not patient identifiers (for example, a clinician's or drug name), and the text is de-identified.</span>
        </label>

        <div className="mt-5 flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          <button type="button" ref={editRef} className="btn-primary" onClick={onEdit}>
            <PencilLine className="h-4 w-4" aria-hidden="true" /> Edit text
          </button>
          <button type="button" className="btn-outline" onClick={onConfirm} disabled={!checked}>
            <ShieldCheck className="h-4 w-4" aria-hidden="true" /> I confirm it's de-identified
          </button>
        </div>
      </div>
    </AiDialog>
  );
}
